import Service from '@/core/services/service';
import { ServicePromise } from '@/core/services/interfaces';
import { getToken } from '@/core/helpers/grecaptcha';
import { Fee } from '@/views/checkout/interfaces';
import api from './config';
import {
    IrisOptions,
    BlikOptions,
    WeroOptions,
    SwishOptions,
    ChargeOptions,
    PaypalOptions,
    KlarnaOptions,
    GiroPayOptions,
    BlueCodeOptions,
    PayconiqOptions,
    SatispayOptions,
    MobilePayOptions,
    ClickToPayOptions,
    BancontactOptions,
    MultibancoOptions,
    BancomatPayOptions,
    PaymentSessionOptions,
    LoyaltySessionOptions,
    CardChargeTokenOptions,
    PaymentInitiationOptions,
    ValidatePaymentCodeOptions,
    SamsungPayTransactionOptions,
    ApplePayAuthenticationOptions
} from './interfaces';

export default {
    getOrder(ref: string, fees?: Array<Fee>): ServicePromise<any> {
        const services = new Service(api);
        const url = 'checkout/v2/paymentdetails';

        const requestConfig = {
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            },
            params: {
                ref
            }
        };

        fees?.forEach((fee) => {
            if (fee.queryParameter) {
                requestConfig.params[fee.queryParameter] = fee.amount;
            }
        });

        return services.getData(url, requestConfig);
    },

    getBanks(ref: string, pageSize: number, currentPage: number, search?: string | null): ServicePromise<any> {
        const services = new Service(api);
        const url = 'checkout/v2/paymentinitiation/banks';

        const requestConfig = {
            params: {
                ref,
                search,
                pageSize,
                currentPage
            }
        };

        return services.getData(url, requestConfig);
    },

    cardInfo(
        ref: string,
        cardNumber?: string | null,
        creditCardId?: string | null,
        token?: string | null
    ): ServicePromise<any> {
        const services = new Service(api);
        const url = 'checkout/v2/cardinfo';

        const requestConfig = {
            headers: {},
            params: {
                ref
            }
        };

        if (cardNumber) {
            requestConfig.headers['card-number'] = cardNumber;
        }

        if (creditCardId) {
            requestConfig.headers['credit-card-id'] = creditCardId;
        }

        if (token) {
            requestConfig.headers['token'] = token;
        }

        return services.getData(url, requestConfig);
    },

    async createChargeToken(options: Partial<CardChargeTokenOptions>) {
        const services = new Service(api);
        const url = '/checkout/v2/chargetokens';

        let captchaToken: string | undefined;
        try {
            const tokenResult = await getToken(process.env.VUE_APP_GOOGLE_RECAPTCHA);

            if (tokenResult.success) {
                captchaToken = tokenResult.data.token;
            }
        } catch {
            // Silent fail-open: surfacing the failure (e.g. via console)
            // would leak a probe signal to an attacker. Backend already
            // logs missing-token cases via RedirectCheckoutCreateChargeToken.
        }

        return services.postData(url, { ...options, captchaToken });
    },

    charge(options: ChargeOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/transactions';

        return services.postData(url, options);
    },

    createApplePayAuthenticationSession(options: ApplePayAuthenticationOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/applepay/sessions';

        return services.postData(url, options);
    },

    createPaypalSession(options: PaypalOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/paypal/sessions';

        return services.postData(url, options);
    },

    createPayconiqSession(options: PayconiqOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/payconiq/sessions';

        return services.postData(url, options);
    },

    createIrisSession(options: IrisOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/iris/sessions';

        return services.postData(url, options);
    },

    createKlarnaSession(options: KlarnaOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/klarna/sessions';

        return services.postData(url, options);
    },

    createSamsungPaySession(options: SamsungPayTransactionOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/samsungpay/sessions';

        return services.postData(url, options);
    },

    createPaymentInitiationSession(options: PaymentInitiationOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/paymentinitiation/sessions';

        return services.postData(url, options);
    },

    getCountries(): ServicePromise<any> {
        const services = new Service(api);
        const url = 'checkout/v2/countries';

        return services.getData(url);
    },

    createMultibancoWaySession(options: MultibancoOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/sibspagamentos/mbwayid/sessions';

        return services.postData(url, options);
    },

    createMultibancoReferenceSession(options: MultibancoOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/sibspagamentos/mbreference/sessions';

        return services.postData(url, options);
    },

    createBancontactDeepLink(options: BancontactOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/bep/deeplink';

        return services.postData(url, options);
    },

    createMobilePaySession(options: MobilePayOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/mobilepay/sessions';

        return services.postData(url, options);
    },

    createSatispaySession(options: SatispayOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/satispay/sessions';

        return services.postData(url, options);
    },

    createBancomatPaySession(options: BancomatPayOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/bancomat/sessions';

        return services.postData(url, options);
    },

    createSwishSession(options: SwishOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/swish/sessions';

        return services.postData(url, options);
    },

    createBlikSession(options: BlikOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/blik/sessions';

        return services.postData(url, options);
    },

    createBlueCodeSession(options: BlueCodeOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/bluecode/sessions';

        return services.postData(url, options);
    },

    getPaymentSessions(options: PaymentSessionOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/paymentsessions';

        return services.postData(url, options);
    },

    encryptClickToPayCard(options: ClickToPayOptions) {
        const services = new Service(api);
        const url = '/checkout/v2/mastercardclicktopay/encryptcard';

        return services.postData(url, options);
    },

    cancelPaymentOrder(orderCode: number) {
        const services = new Service(api);
        const url = `checkout/v2/orders/${orderCode}`;

        return services.deleteData(url);
    },

    createLoyaltySession(options: LoyaltySessionOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/loyalty/sessions';

        return services.postData(url, options);
    },

    cancelLoyaltySession(sessionId: string) {
        const services = new Service(api);
        const url = `checkout/v2/loyalty/sessions/${sessionId}`;

        return services.deleteData(url);
    },

    createGiroPaySession(options: GiroPayOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/giropay/sessions';

        return services.postData(url, options);
    },

    validatePaymentCode(options: ValidatePaymentCodeOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/orders/merchanttrns:validate';

        return services.postData(url, options);
    },

    createWeroSession(options: WeroOptions) {
        const services = new Service(api);
        const url = 'checkout/v2/wero/sessions';

        return services.postData(url, options);
    }
};
